import { inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { UserProfileService } from '../user/user-profile.service';
import { HttpResponse, HttpHeaders, HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, Subject, catchError, map, of, tap, throwError } from 'rxjs';
import {
  ITestContact,
  TInstitution,
  IRole,
  TBlkCxpRole,
  IUserProfile,
  IUserOrganization,
} from '../../types/settingsDataModule';
import { getCountryCode } from '../../../utils/utils';
import { failureReason } from '../../../utils/http-errors';
import { IPagination } from '../../types/contactModule';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  constructor() {}

  // settingsBaseUrl: string = 'https://sandbox.mamba.blackkonnect.io/admin';
  // settingsBaseUrl: string = 'https://api.dev.blackkonnect.io/services/blackkonnectcrm';
  settingsServiceName: string = 'blackkonnectcrm';
  engagementServiceName = 'blackkonnectcxpengagement';
  private readonly iamBaseUrl: string =
    environment.controlTowerApiUrl || environment.mambaApiUrl;

  apiService = inject(ApiService);
  userService = inject(UserProfileService);
  http = inject(HttpClient);

  ErrorMsg: string = '';

  // timeout ID for cleanup
  private errorTimeoutId?: ReturnType<typeof setTimeout>;

  // Surface fetch errors so components can display feedback
  private errorMessage$ = new Subject<{ message: string; source: string }>();

  get onErrorMessage(): Observable<{ message: string; source: string }> {
    return this.errorMessage$.asObservable();
  }

  /**
   * Tells the page why a load failed, in plain words: never the raw
   * "Http failure response … 404" text. `notFound` is what a 404 means here.
   */
  private emitFetchError(err: any, source: string, notFound: string) {
    this.emitLoadProblem(source, failureReason(err, notFound));
  }

  private emitLoadProblem(source: string, message: string) {
    this.updateError(message);
    this.errorMessage$.next({ message, source });
  }

  /** Sent instead of a request that could only fail: the account has no organization on record. */
  private static readonly NOT_LINKED = 'Your account isn’t linked to an organization we can find. Black Konnect support can link it.';

  get organizationId() {
    return this.userService.organizationId;
  }

  get institutionId() {
    return this.userService.getInstitutionId;
  }

  get userDetails() {
    return this.userService.userDetails;
  }

  isFetching: boolean = false;
  isLoading: boolean = false;

  private userProfileData = new BehaviorSubject<IUserProfile | null>(null);
  private userInstitutionData: any =
    new BehaviorSubject<IUserOrganization | null>(null);
  private institutionUsers: any = new BehaviorSubject([]);
  private testContacts: any = new BehaviorSubject([]);
  private testContactCount: any = new BehaviorSubject(0);
  private roles = new BehaviorSubject<IRole[]>([]); //these are all the roles available in IAM
  private userProfileImage = new BehaviorSubject<any>(null);
  private userRoles = new BehaviorSubject<TBlkCxpRole[]>([]);
  private organizationLogo = new BehaviorSubject<any>(null);

  // PAGINATION DATA
  private institutionUserspagination = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });

  // fetch user data
  fetchUserProfile() {
    this.isFetching = true;

    this.apiService
      .get<IUserProfile>(
        `${this.engagementServiceName}/api/dashboard/organization/user/profile/${this.userDetails?.sub}`
      )
      .subscribe({
        next: (data: IUserProfile) => {
          // Roles first, so anything combining roles and profile never sees
          // the new profile with the old (empty) roles.
          if (data.realmRoles && data.realmRoles.length > 0) {
            this.userRoles.next(data.realmRoles);
          }
          this.userProfileData.next(data);
          // this.contactsPaginationData.next(data.itemPagination);
        },
        error: (err) => {
          this.isFetching = false;
          this.emitFetchError(
            err,
            'fetchUserProfile',
            'Your profile wasn’t found. Sign out and back in; if it keeps happening, Black Konnect support can check your account.'
          );
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // fetch user institution/organization data
  fetchUserInstitution() {
    if (!this.institutionId) {
      this.emitLoadProblem('fetchUserInstitution', SettingsService.NOT_LINKED);
      return;
    }
    this.isFetching = true;

    this.apiService
      .getNonBaseApi<IUserOrganization>(
        `${this.iamBaseUrl}/api/iam/customers/${this.institutionId}`
      )
      .subscribe({
        next: (data: any) => {
          this.isFetching = false;
          this.userInstitutionData.next(data);
        },
        error: (err) => {
          this.isFetching = false;
          this.emitFetchError(
            err,
            'fetchUserInstitution',
            SettingsService.NOT_LINKED
          );
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  /** The criteria of the last users fetch, so refreshes keep the search, filter and page. */
  private lastInstitutionUsersParams?: Record<string, any>;

  // fetch users of the logged into institution
  fetchInstitutionUsers(params?: Record<string, any>) {
    this.lastInstitutionUsersParams = params ? { ...params } : undefined;
    if (!this.organizationId) {
      this.emitLoadProblem('fetchInstitutionUsers', SettingsService.NOT_LINKED);
      return;
    }
    this.isFetching = true;

    this.apiService
      .getNonBaseApi<any>(
        `${this.iamBaseUrl}/api/iam/organisations/members/user-profiles/organisations/${this.organizationId}`,
        params
      )
      .subscribe({
        next: (data: any) => {
          this.isFetching = false;
          // Handle both array response and paginated response
          if (Array.isArray(data)) {
            this.institutionUsers.next(data);
            // Set pagination for array response
            this.institutionUserspagination.next({
              totalElements: data.length,
              pageSize: data.length,
              totalPages: 1,
              pageNumber: 0,
              offSet: 0,
            });
          } else {
            this.institutionUsers.next(data.items || data);
            this.institutionUserspagination.next(
              data.itemPagination || {
                totalElements: 0,
                pageSize: 10,
                totalPages: 1,
                pageNumber: 0,
                offSet: 0,
              }
            );
          }
        },
        error: (err) => {
          this.isFetching = false;
          this.emitFetchError(
            err,
            'fetchInstitutionUsers',
            SettingsService.NOT_LINKED
          );
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  /** Refetches the users list with the criteria it was last fetched with. */
  refreshInstitutionUsers() {
    this.fetchInstitutionUsers(this.lastInstitutionUsersParams);
  }

  // fetch roles from IAM
  fetchUserRoles() {
    this.isFetching = true;

    this.apiService
      .getNonBaseApi<any>(
        `${this.iamBaseUrl}/api/iam/roles/cxp`
      )
      .subscribe({
        next: (data: any) => {
          this.isFetching = false;
          this.roles.next(this.normalizeRolesResponse(data));
        },
        error: (err) => {
          this.isFetching = false;
          this.emitFetchError(
            err,
            'fetchUserRoles',
            'The list of roles isn’t available right now. Try again in a minute.'
          );
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  get getUserProfileData() {
    return this.userProfileData.asObservable();
  }

  get getUserInstitutionData() {
    return this.userInstitutionData.asObservable();
  }

  get getInstitutionUsers() {
    return this.institutionUsers.asObservable();
  }

  get getInstitutionUsersPagination() {
    return this.institutionUserspagination.asObservable();
  }

  get rolesData() {
    return this.roles.asObservable();
  }

  get getUserRoles() {
    return this.userRoles.asObservable();
  }

  /**
   * DEV ONLY: seed roles directly, bypassing the profile API. Used by the
   * `mock` configuration's auth stub so permission-gated UI is visible.
   */
  seedDevRoles(roles: TBlkCxpRole[]) {
    this.userRoles.next(roles);
  }

  get getUserProfileImageData() {
    return this.userProfileImage.asObservable();
  }

  get getOrganizationLogoData() {
    return this.organizationLogo.asObservable();
  }

  // -------------------------------------------------------------------------------------------------------------------------------
  // USER MANAGEMENT APIs
  // create user
  createUser(userData: {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    // password: string;
    organisationId?: string;
    // organisationServiceAccountId?: string;
    productCategory?: string;
    authorities: string[];
  }): Observable<any> {
    this.isLoading = true;
    return (
      this.apiService
        // ${this.iamBaseUrl}/api/iam/organisations/members
        .postNonBaseApi(
          `${this.iamBaseUrl}/api/iam/organisations/members`,
          {
            ...userData,
            organisationId: this.organizationId,
            organisationServiceAccountId: this.organizationId,
            productCategory: 'BLACKKONNECT_CXP_PORTAL',
          }
        )
        .pipe(
          tap({
            next: () => {
              // User created: refresh the list, keeping its search, filter and page
              this.refreshInstitutionUsers();
            },
            error: (err) => {
              this.isLoading = false;
            },
            complete: () => {
              this.isLoading = false;
            },
          })
        )
    );
  }

  // update user profile
  updateUserProfile(
    subjectId: string,
    userData: {
      firstName: string;
      lastName: string;
      emailAddress: string;
      phoneNumber: string;
      authorities?: string[];
    }
  ): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .putNonBaseApi(
        `${this.iamBaseUrl}/api/iam/organisations/members/user-profiles/user-subjects/${subjectId}`,
        userData
      )
      .pipe(
        tap({
          next: () => {
            // Refresh user profile data after successful update
            this.fetchUserProfile();
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // update user password - this is when user is logged in and wants to change password
  updateUserPassword(
    userId: string,
    passwordData: {
      currentPassword: string;
      newPassword: string;
    }
  ): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .putNonBaseApi(
        `${this.iamBaseUrl}/api/iam/users/${userId}/password`,
        passwordData
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // request password reset
  requestPasswordReset(email: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .postNonBaseApi(
        `${this.iamBaseUrl}/api/auth/forgot-password`,
        { email }
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // verify reset password token
  verifyResetPasswordToken(token: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .getNonBaseApi(
        // Encoded: reset tokens can carry +, / and = which would otherwise be misread.
        `${this.iamBaseUrl}/api/auth/verify-reset-token?token=${encodeURIComponent(token)}`
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // reset user password with token this is used when the user receives a reset password link in their email
  resetUserPassword(token: string, newPassword: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .postNonBaseApi(
        `${this.iamBaseUrl}/api/auth/reset-password`,
        { token, newPassword }
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // disable user
  disableUser(userId: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .putNonBaseApi(
        `${this.iamBaseUrl}/api/iam/users/${userId}/disable`,
        {}
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
            // Refresh the list, keeping its search, filter and page
            this.refreshInstitutionUsers();
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // enable user
  enableUser(userId: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .putNonBaseApi(
        `${this.iamBaseUrl}/api/iam/users/${userId}/enable`,
        {}
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
            // Refresh the list, keeping its search, filter and page
            this.refreshInstitutionUsers();
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // add user profile image
  addUserProfileImage(userId: string, imageFile: File): Observable<any> {
    this.isLoading = true;

    const formData = new FormData();
    formData.append('file', imageFile);

    // Don't set Content-Type header - browser will set it automatically with boundary
    const headers = new HttpHeaders();

    return this.apiService
      .postNonBaseApi(
        `${this.iamBaseUrl}/api/iam/users/${userId}/profile-image`,
        formData,
        headers
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // get user profile image
  getUserProfileImage(userId: string): Observable<Blob> {
    this.isFetching = true;
    // Add a cache-busting query so clients do not keep stale avatars after upload.
    const url = `${this.iamBaseUrl}/api/iam/users/${userId}/profile-image?t=${Date.now()}`;

    // Use HttpClient directly with responseType: 'blob' to handle binary data
    return this.http.get(url, { responseType: 'blob' }).pipe(
      // No photo yet answers 404: that is "no image", not a failure.
      catchError((err) => (err?.status === 404 ? of(null) : throwError(() => err))),
      tap({
        next: (imageBlob: Blob | null) => {
          this.isFetching = false;
          const previousUrl = this.userProfileImage.value;
          if (typeof previousUrl === 'string' && previousUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previousUrl);
          }
          if (!imageBlob || !imageBlob.size) {
            this.userProfileImage.next(null);
            return;
          }
          try {
            // Create an object URL from the blob
            const objectUrl = URL.createObjectURL(imageBlob);
            this.userProfileImage.next(objectUrl);
          } catch (err) {
            this.updateError(
              (err as any)?.message || 'Error processing profile image'
            );
          }
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err?.message || 'Error fetching profile image');
        },
        complete: () => {
          this.isFetching = false;
        },
      })
    ) as Observable<Blob>;
  }

  // remove user profile image
  removeUserProfileImage(userId: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .deleteNonBaseApi(
        `${this.iamBaseUrl}/api/iam/users/${userId}/profile-image`
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
            // Clear the profile image from state
            const previousUrl = this.userProfileImage.value;
            if (typeof previousUrl === 'string' && previousUrl.startsWith('blob:')) {
              URL.revokeObjectURL(previousUrl);
            }
            this.userProfileImage.next(null);
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // update organization details
  updateOrganizationDetails(
    organizationId: string,
    organizationData: {
      name?: string;
      alias?: string;
      referenceId?: string;
      accountName?: string;
      serviceAccountId?: string;
      keycloakOrgId?: string;
      countryCode?: string;
      website?: string;
      logoImageReference?: string;
      vatNumber?: string;
      taxNumber?: string;
      primaryContactNumber?: string;
      emailAddress?: string;
      registrationNumber?: string;
      description?: string;
      status?: string;
      industryType?: string;
      recordStatus?: string;
    }
  ): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .patchNonBaseApi(
        `${this.iamBaseUrl}/api/iam/customers/${organizationId}`,
        organizationData
      )
      .pipe(
        tap({
          next: () => {
            // Refresh organization data after successful update
            this.fetchUserInstitution();
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // add organization logo
  addOrganizationLogo(imageFile: File): Observable<any> {
    this.isLoading = true;

    const formData = new FormData();
    formData.append('file', imageFile);

    // Don't set Content-Type header - browser will set it automatically with boundary
    const headers = new HttpHeaders();

    return this.apiService
      .postNonBaseApi(
        `${this.iamBaseUrl}/api/iam/organisations/${this.organizationId}/logo`,
        formData,
        headers
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            throw err;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // get organization logo
  getOrganizationLogo(): Observable<Blob> {
    this.isFetching = true;
    const url = `${this.iamBaseUrl}/api/iam/organisations/${this.organizationId}/logo`;

    // Use HttpClient directly with responseType: 'blob' to handle binary data
    return this.http.get(url, { responseType: 'blob' }).pipe(
      // No logo yet answers 404: that is "no image", not a failure.
      catchError((err) => (err?.status === 404 ? of(null) : throwError(() => err))),
      tap({
        next: (imageBlob: Blob | null) => {
          this.isFetching = false;
          if (!imageBlob || !imageBlob.size) {
            this.organizationLogo.next(null);
            return;
          }
          try {
            // Create an object URL from the blob
            const objectUrl = URL.createObjectURL(imageBlob);
            this.organizationLogo.next(objectUrl);
          } catch (err) {
            this.updateError(
              (err as any)?.message || 'Error processing organization logo'
            );
          }
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err?.message || 'Error fetching organization logo');
        },
        complete: () => {
          this.isFetching = false;
        },
      })
    ) as Observable<Blob>;
  }

  // remove organization logo
  removeOrganizationLogo(): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .deleteNonBaseApi(
        `${this.iamBaseUrl}/api/iam/organisations/${this.organizationId}/logo`
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
            // Clear the organization logo from state
            this.organizationLogo.next(null);
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // -------------------------------------------------------------------------------------------------------------------------------
  // TEST CONTACTS APIs
  // create test contact
  createTestContact(testContact: ITestContact): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .post(
        `${this.engagementServiceName}/api/organization/${this.organizationId}/message-test-contacts`,
        {
          firstName: testContact.firstName,
          middleName: '',
          lastName: testContact.lastName,
          // fullName: 'string',
          // nickName: 'string',
          // address: 'string',
          // addressLine2: 'string',
          // region: 'string',
          zipCode: testContact.zipCode || '',
          city: testContact.city,
          country: testContact.country,
          countryCode: getCountryCode(testContact.country!)?.countryCode,
          gender: testContact.gender,
          birthDate: testContact.birthDate,
          // profilePicture: 'string',
          primaryContactNumber: testContact.primaryContactNumber,
          emailAddress: testContact.emailAddress,
          // origin: 'string',
          organisationId: this.organizationId,
        }
      )
      .pipe(
        tap({
          next: () => {
            // Refresh the shared, unfiltered list the composer's "Send test" picker reads,
            // as update and delete do. The settings page reloads its own list.
            this.fetchTestContacts();
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // fetch test contacts
  fetchTestContacts(params?: Record<string, any>) {
    this.isFetching = true;

    this.apiService
      .get<HttpResponse<ITestContact[]>>(
        `${this.engagementServiceName}/api/organization/${this.organizationId}/message-test-contacts`,
        params
      )
      .subscribe({
        next: (data: any) => {
          this.isFetching = false;
          this.testContacts.next(data);
        },
        error: (err) => {
          this.isFetching = false;
          this.emitFetchError(
            err,
            'fetchTestContacts',
            SettingsService.NOT_LINKED
          );
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // fetch test contacts
  fetchTestContactsCount() {
    this.isFetching = true;

    this.apiService
      .get<HttpResponse<ITestContact[]>>(
        `${this.engagementServiceName}/api/organization/${this.organizationId}/message-test-contacts/count`
      )
      .subscribe({
        next: (data: any) => {
          this.isFetching = false;
          this.testContactCount.next(data);
        },
        error: (err) => {
          this.isFetching = false;
          this.emitFetchError(
            err,
            'fetchTestContactsCount',
            SettingsService.NOT_LINKED
          );
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // ── Side-effect-free reads for the Settings > Test numbers page ──
  // They touch neither the shared testContacts cache (the composer's picker
  // reads it) nor the shared isFetching flag, so a search on Settings never
  // leaks into the composer and other skeletons never flicker.

  /** One page of test contacts matching the criteria. */
  listTestContacts(params?: Record<string, any>): Observable<ITestContact[]> {
    return this.apiService
      .get<ITestContact[]>(
        `${this.engagementServiceName}/api/organization/${this.organizationId}/message-test-contacts`,
        params
      )
      .pipe(map((data: any) => (Array.isArray(data) ? data : data?.items || data?.content || [])));
  }

  /** How many test contacts match the criteria (same filters as the list, no paging). */
  countTestContacts(params?: Record<string, any>): Observable<number> {
    return this.apiService
      .get<number>(
        `${this.engagementServiceName}/api/organization/${this.organizationId}/message-test-contacts/count`,
        params
      )
      .pipe(map((count: any) => Number(count) || 0));
  }

  // Update a test-contact
  updateTestContact(testContact: ITestContact) {
    this.isLoading = true;
    return this.apiService
      .patch(
        `${this.engagementServiceName}/api/message-test-contacts/${testContact.id}`,
        {
          ...testContact,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.fetchTestContacts(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: (error) => {
            this.isLoading = false;
            // console.error('Error updating contact', error);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete a contact via api
  deleteTestContact(id: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .delete(`${this.engagementServiceName}/api/message-test-contacts/${id}`)
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.fetchTestContacts();
            // this.fetchContacts(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  get getTestContacts() {
    return this.testContacts.asObservable();
  }

  get getTestContactsCount() {
    return this.testContactCount.asObservable();
  }

  private updateError(msg: string) {
    // Clear existing timeout before setting a new one
    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
    }
    this.ErrorMsg = msg;
    this.errorTimeoutId = setTimeout(() => {
      this.ErrorMsg = '';
      this.errorTimeoutId = undefined;
    }, 5000);
  }

  private normalizeRolesResponse(data: any): IRole[] {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.content)) return data.content;
    if (Array.isArray(data?.roles)) return data.roles;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  }
}
